import { FormDialog } from '@/components/FormDialog'
import { ImageUpload } from '@/components/ImageUpload'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetDepartmentItems } from '@/features/departments/hooks/useDepartment'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { isApiError } from '@/utils/error'
import { formatBuildingFloorLocation } from '@/utils/location'
import { showError, showSuccess } from '@/utils/toast'
import { Camera } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateRepair } from '../hooks/useRepair'

const emptyForm = {
  departmentId: '',
  itemId: '',
  title: '',
  description: '',
  priority: 'Medium',
}
const selectTriggerClassName = 'w-full bg-secondary/50 text-xs'

export function CreateRepairDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation()
  const { departments: userDepartments } = useAuth()
  const { repairPriorities } = useUI()
  const { data: configs } = useGetConfigs()
  const [form, setForm] = useState(emptyForm)
  const [imageIds, setImageIds] = useState<string[]>([])
  const { data: departmentItems, isLoading: isLoadingItems } = useGetDepartmentItems(form.departmentId, {
    status: 'Available',
  })
  const createRepair = useCreateRepair()
  const imageField = useFileUploadField({
    value: imageIds,
    onChange: setImageIds,
    maxFiles: 5,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })

  const reportableItems = useMemo(() => departmentItems?.items ?? [], [departmentItems])
  const selectedItem = reportableItems.find(item => item.id === form.itemId)
  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]
  const departmentGroups = useMemo<SearchableSelectGroup[]>(() => {
    const departmentOptions = userDepartments.map(department => ({
      value: department.id,
      label: department.name,
      description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
      searchValue: `${department.name} ${department.buildingName || ''} ${department.floorName || ''}`,
    }))

    return [
      {
        label: t('scans.mainWarehouse'),
        options: departmentOptions.filter(department => department.value === warehouseDepartmentId),
      },
      {
        label: t('scans.otherDepartments'),
        options: departmentOptions.filter(department => department.value !== warehouseDepartmentId),
      },
    ]
  }, [t, userDepartments, warehouseDepartmentId])
  const itemOptions = reportableItems.map(item => ({
    value: item.id,
    label: `${item.productName || '-'} - ${item.serialNumber}`,
    searchValue: `${item.productName || ''} ${item.serialNumber || ''}`,
  }))

  const isInvalid = !form.departmentId || !form.itemId || !form.title || !form.description
  const isSubmitting = createRepair.isPending || imageField.isUploading

  useEffect(() => {
    if (!open) {
      setForm(emptyForm)
      setImageIds([])
    }
  }, [open])

  function handleDepartmentChange(departmentId: string) {
    setForm(current => ({
      ...current,
      departmentId,
      itemId: '',
    }))
  }

  async function handleSubmit() {
    if (isInvalid) return

    try {
      await createRepair.mutateAsync({
        itemId: form.itemId,
        title: form.title,
        description: form.description,
        priority: form.priority,
        imageIds,
      })
      setForm(emptyForm)
      setImageIds([])
      showSuccess(t('repairs.createSuccess'))
      onOpenChange(false)
    } catch (error) {
      showError(isApiError(error) ? error.message : t('repairs.createError'))
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('repairs.createTitle')}
      submitLabel={isSubmitting ? t('common.submitting') : t('repairs.submit')}
      onSubmit={handleSubmit}
      isLoading={isSubmitting}
      isInvalid={isInvalid}
      contentTestId="repair-dialog"
      submitTestId="repair-form-submit"
    >
      <div className="flex flex-col gap-4 pt-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.placeholder.selectDepartment')} *</Label>
          <SearchableSelect
            value={form.departmentId}
            onValueChange={handleDepartmentChange}
            groups={departmentGroups}
            placeholder={t('common.placeholder.selectDepartment')}
            searchPlaceholder={t('common.placeholder.searchDepartment')}
            emptyText={t('common.noDepartment')}
            triggerClassName={selectTriggerClassName}
            testId="repair-form-department"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.placeholder.selectEquipment')} *</Label>
          <SearchableSelect
            value={form.itemId}
            onValueChange={itemId => setForm({ ...form, itemId })}
            options={itemOptions}
            placeholder={
              form.departmentId ? t('repairs.selectRepairEquipment') : t('common.placeholder.selectDepartmentFirst')
            }
            searchPlaceholder={t('common.placeholder.searchEquipment')}
            emptyText={isLoadingItems ? t('common.loading') : t('repairs.noEquipment')}
            disabled={!form.departmentId}
            triggerClassName={selectTriggerClassName}
            testId="repair-form-item"
          />
        </div>
        {selectedItem && (
          <SelectedEquipmentCard
            name={selectedItem.productName}
            serialNumber={selectedItem.serialNumber}
            departmentName={selectedItem.departmentName}
            buildingName={selectedItem.buildingName}
            floorName={selectedItem.floorName}
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('repairs.title')} *</Label>
            <Input
              data-testid="repair-form-title"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              className="h-9 bg-secondary/50 text-xs"
              placeholder={t('repairs.titlePlaceholder')}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('repairs.priority')}</Label>
            <Select value={form.priority} onValueChange={priority => setForm({ ...form, priority })}>
              <SelectTrigger data-testid="repair-form-priority" className="h-9 w-full bg-secondary/50 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card text-foreground">
                {repairPriorities?.map(priority => (
                  <SelectItem data-testid={`repair-form-priority-option-${priority.value}`} key={priority.value} value={priority.value}>
                    {priority.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('repairs.description')} *</Label>
          <Textarea
            data-testid="repair-form-description"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="bg-secondary/50 text-xs"
            rows={3}
            placeholder={t('repairs.descriptionPlaceholder')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="flex items-center gap-1 text-xs">
            <Camera className="size-3" />
            {t('repairs.attachImages')}
          </Label>
          <ImageUpload
            files={imageField.files}
            imageIds={imageField.imageIds}
            onChange={imageField.handleChange}
            onRemoveExisting={imageField.handleRemoveExisting}
            maxImages={5}
          />
          {imageField.isUploading ? (
            <p className="text-[11px] text-muted-foreground">
              {t('common.uploadingImages')}
              {imageField.uploadProgress ? ` ${imageField.uploadProgress}%` : '...'}
            </p>
          ) : null}
        </div>
      </div>
    </FormDialog>
  )
}
